import {
  Component,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { Router, RouterLink } from "@angular/router";
import { animate, style, transition, trigger } from "@angular/animations";
import { DomSanitizer } from "@angular/platform-browser";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { MenuModule } from "headlessui-angular";
import { FormsModule } from "@angular/forms";
import { NgClass, NgFor, NgIf } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { IconModule } from "@crm-workspace/common-lib";
import { AppService } from "../services/app.service";
import { AuthService } from "../services/auth.service";
import {
  AppNotification,
  NotificationService,
} from "../services/notification/notification.service";
import { environment } from "../../environments/environment";

type NotificationFilter = "all" | "unread" | "read";
type NotificationVisualType =
  | "payment"
  | "success"
  | "warning"
  | "failure"
  | "booking"
  | "document"
  | "reminder"
  | "user"
  | "system";

interface NotificationGroup {
  label: "Today" | "Yesterday" | "Earlier";
  notifications: AppNotification[];
}

@Component({
  selector: "header",
  templateUrl: "./header.html",
  styleUrls: ["./header.css"],
  animations: [
    trigger("toggleAnimation", [
      transition(":enter", [
        style({ opacity: 0, transform: "scale(0.95)" }),
        animate("100ms ease-out", style({ opacity: 1, transform: "scale(1)" })),
      ]),
      transition(":leave", [
        animate("75ms", style({ opacity: 0, transform: "scale(0.95)" })),
      ]),
    ]),
  ],
  standalone: true,
  imports: [
    NgClass,
    RouterLink,
    FormsModule,
    NgIf,
    NgFor,
    MenuModule,
    TranslateModule,
    IconModule,
  ],
})
export class HeaderComponent implements OnInit {
  public readonly notificationsService = inject(NotificationService);
  private readonly authService = inject(AuthService);

  explorePropertiesUrl = environment.explorePropertiesUrl;
  store: any;
  isScrolled = false;
  search = false;
  activeDropdown: string[] = [];
  messages = [
    {
      id: 1,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-success-light dark:bg-success text-success dark:text-success-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>`,
      ),
      title: "Congratulations!",
      message: "Your OS has been updated.",
      time: "1hr",
    },
    {
      id: 2,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-info-light dark:bg-info text-info dark:text-info-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>`,
      ),
      title: "Did you know?",
      message: "You can switch between artboards.",
      time: "2hr",
    },
    {
      id: 3,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-danger-light dark:bg-danger text-danger dark:text-danger-light"> <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></span>`,
      ),
      title: "Something went wrong!",
      message: "Send Reposrt",
      time: "2days",
    },
    {
      id: 4,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-warning-light dark:bg-warning text-warning dark:text-warning-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">    <circle cx="12" cy="12" r="10"></circle>    <line x1="12" y1="8" x2="12" y2="12"></line>    <line x1="12" y1="16" x2="12.01" y2="16"></line></svg></span>`,
      ),
      title: "Warning",
      message: "Your password strength is low.",
      time: "5days",
    },
  ];

  unreadNotifications = computed(() =>
    this.notificationsService.unreadNotifications(),
  );
  readNotifications = computed(() =>
    this.notificationsService.readNotifications(),
  );
  unreadCount = computed(() => this.notificationsService.unreadCount());
  notificationFilter = signal<NotificationFilter>("all");
  allNotifications = computed(() =>
    [...this.unreadNotifications(), ...this.readNotifications()].sort(
      (first, second) =>
        new Date(second.createdAt).getTime() -
        new Date(first.createdAt).getTime(),
    ),
  );
  filteredNotifications = computed(() => {
    switch (this.notificationFilter()) {
      case "unread":
        return this.unreadNotifications();
      case "read":
        return this.readNotifications();
      default:
        return this.allNotifications();
    }
  });
  groupedNotifications = computed(() =>
    groupNotificationsByDate(this.filteredNotifications()),
  );
  hasFilteredNotifications = computed(
    () => this.filteredNotifications().length > 0,
  );
  isLoadingMore = computed(() => {
    const filter = this.notificationFilter();
    if (filter === "unread") {
      return this.notificationsService.unreadLoadingMore();
    }
    if (filter === "read") {
      return this.notificationsService.readLoadingMore();
    }
    return (
      this.notificationsService.unreadLoadingMore() ||
      this.notificationsService.readLoadingMore()
    );
  });

  constructor(
    public translate: TranslateService,
    public storeData: Store<any>,
    public router: Router,
    private appSetting: AppService,
    private sanitizer: DomSanitizer,
    private dialogRef: MatDialog,
  ) {
    this.initStore();
  }

  get currentUser() {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch (e) {
      console.error("Error parsing user from localStorage", e);
    }
    return null;
  }

  getFirstLetter(): string {
    const user = this.currentUser;
    if (user?.first_name) {
      return user.first_name.charAt(0);
    }
    if (user?.display_name) {
      return user.display_name.charAt(0);
    }
    return "C";
  }

  logout(): void {
    this.authService.logout({
      source: 'HeaderComponent.logout',
      reason: 'user_clicked_header_logout',
    });
  }

  async initStore() {
    this.storeData
      .select((d) => d.index)
      .subscribe((d) => {
        this.store = d;
      });
  }

  ngOnInit() {
        this.initStore();
    this.updateScrolled();
    this.notificationsService.setTargetPortal("CUSTOMER");
    this.notificationsService.setCustomerId(
      this.authService.getAuthUserId() ||
        this.notificationsService.resolveCustomerIdFromAuth(),
    );
    this.notificationsService.refresh();
  }

  @HostListener("window:scroll")
  onWindowScroll(): void {
    this.updateScrolled();
  }

  private updateScrolled(): void {
    const y =
      document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.isScrolled = y > 8;
  }

  toggleDropdown(value: string) {
    if (this.activeDropdown.includes(value)) {
      this.activeDropdown = this.activeDropdown.filter((d) => d !== value);
    } else {
      this.activeDropdown.push(value);
    }
  }

  markNotificationRead(id: string, event: Event): void {
    event.stopPropagation();
    this.notificationsService.markAsRead(id);
  }

  markAllNotificationsRead(event: Event): void {
    event.stopPropagation();
    this.notificationsService.markAllAsRead();
  }

  setNotificationFilter(filter: NotificationFilter, event: Event): void {
    event.stopPropagation();
    this.notificationFilter.set(filter);
  }

  getNotificationType(notification: AppNotification): NotificationVisualType {
    const content =
      `${notification.title} ${notification.body} ${notification.message ?? ""}`.toLowerCase();

    if (/(payment|demand|invoice|receipt|amount|₹|rupee)/.test(content)) {
      return "payment";
    }
    if (/(approved|success|completed|confirmed)/.test(content)) {
      return "success";
    }
    if (/(rejected|failed|cancelled|declined|error)/.test(content)) {
      return "failure";
    }
    if (/(warning|urgent|overdue|attention)/.test(content)) {
      return "warning";
    }
    if (/(booking|property|unit|home)/.test(content)) {
      return "booking";
    }
    if (/(document|file|letter|agreement)/.test(content)) {
      return "document";
    }
    if (/(reminder|scheduled|due|clock)/.test(content)) {
      return "reminder";
    }
    if (/(user|lead|customer|agent)/.test(content)) {
      return "user";
    }
    return "system";
  }

  onNotificationScroll(event: Event): void {
    const service = this.notificationsService;
    if (!service.isReady() || service.loading()) {
      return;
    }

    const el = event.target as HTMLElement | null;
    if (!el) {
      return;
    }

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (!nearBottom) {
      return;
    }

    switch (this.notificationFilter()) {
      case "unread":
        if (service.unreadHasMore()) {
          service.loadMoreUnread();
        }
        break;
      case "read":
        if (service.readHasMore()) {
          service.loadMoreRead();
        }
        break;
      default:
        if (service.unreadHasMore()) {
          service.loadMoreUnread();
        }
        if (service.readHasMore()) {
          service.loadMoreRead();
        }
        break;
    }
  }
}

function groupNotificationsByDate(
  notifications: AppNotification[],
): NotificationGroup[] {
  const groups = new Map<NotificationGroup["label"], AppNotification[]>([
    ["Today", []],
    ["Yesterday", []],
    ["Earlier", []],
  ]);
  const today = startOfDay(new Date());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  for (const notification of notifications) {
    const createdDay = startOfDay(new Date(notification.createdAt));
    let label: NotificationGroup["label"] = "Earlier";
    if (createdDay.getTime() === today.getTime()) {
      label = "Today";
    } else if (createdDay.getTime() === yesterday.getTime()) {
      label = "Yesterday";
    }
    groups.get(label)?.push(notification);
  }

  return Array.from(groups.entries())
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, notifications: items }));
}

function startOfDay(value: Date): Date {
  if (Number.isNaN(value.getTime())) {
    return new Date(0);
  }
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}
